import type {
  AnchorHTMLAttributes,
  FocusEvent,
  MouseEvent,
  PointerEvent,
  ReactElement,
  TouchEvent,
} from 'react';
import { navigate } from '../router';
import { prefetchRouteData } from '../route-data';

export interface LinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string;
  replace?: boolean;
}

/**
 * Router-aware anchor: plain <a href> semantics (middle-click, cmd-click and
 * copy-link all behave), left-click intercepted into pushState navigation.
 */
export function Link({
  to,
  replace,
  onClick,
  onFocus,
  onPointerEnter,
  onTouchStart,
  children,
  target,
  ...rest
}: LinkProps): ReactElement {
  const prefetch = (): void => prefetchRouteData(to);
  const handleClick = (e: MouseEvent<HTMLAnchorElement>): void => {
    onClick?.(e);
    if (e.defaultPrevented) return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (target && target !== '_self') return;
    e.preventDefault();
    navigate(to, { replace });
  };
  const handleFocus = (e: FocusEvent<HTMLAnchorElement>): void => {
    onFocus?.(e);
    if (!e.defaultPrevented) prefetch();
  };
  const handlePointerEnter = (e: PointerEvent<HTMLAnchorElement>): void => {
    onPointerEnter?.(e);
    if (!e.defaultPrevented) prefetch();
  };
  const handleTouchStart = (e: TouchEvent<HTMLAnchorElement>): void => {
    onTouchStart?.(e);
    if (!e.defaultPrevented) prefetch();
  };
  return (
    <a
      href={to}
      target={target}
      onClick={handleClick}
      onFocus={handleFocus}
      onPointerEnter={handlePointerEnter}
      onTouchStart={handleTouchStart}
      {...rest}
    >
      {children}
    </a>
  );
}
